// Reading accessibility (migration 019).
//
// Lantern exists for children who find learning hard, and a meaningful share
// of those children are dyslexic or dyspraxic, or have ADHD or a visual
// processing difference. These helpers turn the parent's choices — delivered
// on /session-config as `accessibility` — into the values the reading
// surfaces apply.
//
// CommonJS so the server jest suite can require it directly, matching the
// existing utils/sessionTimer.js convention.

// Built from faces that ship with the operating systems children actually
// use, rather than a webfont. That matters more than it sounds: the PWA has
// to work on a poor connection, a webfont is another request that can hang,
// and self-hosting OpenDyslexic raises a licensing question nobody has
// answered. Sans-serif faces with wide letter spacing and clearly
// differentiated letterforms carry most of the real-world benefit.
const DYSLEXIA_FONT_STACK =
  "Verdana, Tahoma, 'Trebuchet MS', 'Segoe UI', Arial, sans-serif";

const LINE_HEIGHTS = {
  normal:  1.5,
  relaxed: 1.9,
};

// Everything here reproduces the pre-migration appearance exactly, so a child
// whose parent has not touched the settings sees no change at all.
const DEFAULTS = {
  readingFont:  'default',
  textScale:    100,
  lineSpacing:  'normal',
  reduceMotion: false,
};

// Never throws and never returns a partial object. A missing or malformed
// payload — an older cached client, a failed config fetch — must degrade to
// today's appearance rather than leaving a child with unstyled text.
function normaliseAccessibility(raw) {
  const a = raw && typeof raw === 'object' ? raw : {};
  return {
    readingFont:  a.readingFont === 'dyslexia' ? 'dyslexia' : DEFAULTS.readingFont,
    textScale:    [100, 125, 150].includes(a.textScale) ? a.textScale : DEFAULTS.textScale,
    lineSpacing:  a.lineSpacing === 'relaxed' ? 'relaxed' : DEFAULTS.lineSpacing,
    reduceMotion: a.reduceMotion === true,
  };
}

/**
 * Style object to spread onto a text container.
 *
 * `baseFont` is the font the surface would otherwise use, so callers keep
 * their own typography when the child is on the default setting — this only
 * overrides when a parent has actively chosen the dyslexia-friendly stack.
 */
function accessibilityStyles(settings, baseFont) {
  const a = normaliseAccessibility(settings);
  const style = {
    lineHeight: LINE_HEIGHTS[a.lineSpacing],
  };
  if (a.readingFont === 'dyslexia') {
    style.fontFamily = DYSLEXIA_FONT_STACK;
    // Dyslexia guidance consistently favours slightly looser tracking; this
    // is small on purpose, since too much harms word recognition.
    style.letterSpacing = '0.02em';
  } else if (baseFont) {
    style.fontFamily = baseFont;
  }
  if (a.textScale !== 100) {
    // A percentage on the container, so every nested rem/em and inherited
    // size scales together instead of each one needing its own override.
    style.fontSize = `${a.textScale}%`;
  }
  return style;
}

/**
 * True when animation should be suppressed.
 *
 * Honours the operating system's own reduced-motion preference as well as the
 * parent's setting: a child who has already asked their device to calm
 * animations down should not have to ask twice.
 */
function shouldReduceMotion(settings) {
  if (normaliseAccessibility(settings).reduceMotion) return true;
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // matchMedia can throw in odd embedded webviews; motion is the safe
    // default because it is the current behaviour.
    return false;
  }
}

module.exports = {
  DEFAULTS,
  DYSLEXIA_FONT_STACK,
  LINE_HEIGHTS,
  normaliseAccessibility,
  accessibilityStyles,
  shouldReduceMotion,
};
